import EcoLogo from "./EcoLogo";
import { Link, useNavigate } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import PlatformTour from "./PlatformTour";

const megaMenu = [
  {
    id: "physical", label: "Physical", color: "#16a34a", basePath: "products",
    sections: [
      { title: "Cables & Wires", categoryKey: "cables", items: ["EV Charging Cables", "Solar DC Cables", "House Wires", "Multicore Cables", "Submersible Cables", "CCTV Cables"] },
      { title: "Chargers & Inverters", categoryKey: "chargers", items: ["AC Chargers", "DC Fast Chargers", "Solar Inverters", "Hybrid Inverters", "EV Connectors"] },
      { title: "Solar", categoryKey: "solar", items: ["Solar Panels", "Solar Pumps", "Solar Kits", "Mounting Structures", "Bifacial Panels"] },
      { title: "Batteries & IoT", categoryKey: "batteries", items: ["Lithium Batteries", "Lead Acid Batteries", "Smart Gateways", "Energy Meters", "Data Loggers"] },
      { title: "Spare Parts", categoryKey: "spare-parts", items: ["EV Spare Parts", "Solar Spare Parts", "Inverter Parts", "Charger Accessories"] },
      { title: "Vehicles", categoryKey: "vehicles", items: ["2-Wheeler", "3-Wheeler"] },
    ],
  },
  {
    id: "digital", label: "Digital", color: "#0891b2", basePath: "digital",
    sections: [
      { title: "Asset Management", categoryKey: "asset-management", items: ["Fleet Tracking", "Device Registry", "Lifecycle Management", "Audit Trails"] },
      { title: "Charger Management", categoryKey: "charger-management", items: ["OCPP Integration", "Remote Control", "Session Analytics", "Revenue Reports"] },
      { title: "Electricity Billing", categoryKey: "electricity-billing", items: ["Automated Invoicing", "Tariff Management", "Payment Gateway", "Subsidy Tracking"] },
      { title: "Energy Management", categoryKey: "energy-management", items: ["Real-time Dashboards", "Predictive Alerts", "Carbon Tracking", "Performance Reports"] },
    ],
  },
  {
    id: "service", label: "Service", color: "#7c3aed", basePath: "services",
    sections: [
      { title: "EPC Partners", categoryKey: "epc-partners", items: ["Solar EPC", "EV Infrastructure EPC", "Grid Connection", "Turnkey Projects"] },
      { title: "Electricians", categoryKey: "electricians", items: ["Charger Installation", "Panel Wiring", "Solar Setup", "Emergency Repairs"] },
      { title: "Workshops & AMC", categoryKey: "workshops-amc", items: ["EV Workshop", "Solar AMC", "Inverter Servicing", "Remote Diagnostics"] },
      { title: "Repair Centres", categoryKey: "repair-centres", items: ["Battery Repair", "Charger Repair", "Inverter Repair", "IoT Device Support"] },
    ],
  },
  { id: "execution", label: "Execution", color: "#ea580c", basePath: "execution", type: "project", sections: [] },
  { id: "applications", showArrow: false, directLink: "/applications", label: "Applications", color: "#0f766e", basePath: "applications", sections: [] },
  { id: "recycling", showArrow: false, directLink: "/recycling", label: "Recycling", color: "#16a34a", basePath: "recycling", sections: [] },
];

const linkStyle = {
  color: "rgba(255,255,255,0.75)", fontSize: "12px", cursor: "pointer",
  background: "none", border: "none", fontFamily: "inherit",
};

const roles = [
  {
    id: "buyer", emoji: "🛒", title: "I'm a Buyer",
    subtitle: "Looking for EV, Solar or IoT products",
    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0",
    features: ["Browse 10,000+ verified products", "Compare prices from multiple vendors", "Get instant quotes", "Track your enquiries"],
    cta: "Browse Products", path: "/products",
    fun: "Let's find your perfect energy setup! ⚡",
  },
  {
    id: "vendor", emoji: "🏭", title: "I'm a Vendor",
    subtitle: "I want to sell my products here",
    color: "#0891b2", bg: "#f0f9ff", border: "#bae6fd",
    features: ["List your products for free", "Reach 1000s of buyers", "Zero commission model", "Get verified badge"],
    cta: "Become a Vendor", path: "/contact",
    fun: "Join India's fastest growing energy marketplace! 🚀",
  },
  {
    id: "partner", emoji: "🤝", title: "I'm a Partner",
    subtitle: "EPC, installer or service provider",
    color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe",
    features: ["Get project leads directly", "Work with enterprise clients", "Verified partner badge", "Priority support"],
    cta: "Become a Partner", path: "/contact",
    fun: "Power India's green transition with us! 🌱",
  },
];

function OnboardingModal({ onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [animating, setAnimating] = useState(false);

  const role = roles.find(r => r.id === selected);

  const handleSelect = (id) => {
    setSelected(id);
    setAnimating(true);
    setTimeout(() => {
      setStep(2);
      setAnimating(false);
    }, 400);
  };

  const handleCta = () => {
    onClose();
    navigate(role.path);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(15,23,42,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
      animation: "fadeIn 0.3s ease",
    }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "white", borderRadius: 24,
        width: "100%", maxWidth: 560,
        boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
        overflow: "hidden",
        animation: "slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        <div style={{
          background: "linear-gradient(135deg, #1a1a2e, #16213e)",
          padding: "28px 32px 24px",
          position: "relative",
        }}>
          <button onClick={onClose} style={{
            position: "absolute", top: 16, right: 16,
            background: "rgba(255,255,255,0.1)", border: "none",
            borderRadius: "50%", width: 32, height: 32,
            color: "white", fontSize: 16, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>

          {step === 1 ? (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 900, color: "white", margin: "0 0 6px" }}>
                Welcome to EcoConnect!
              </h2>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                Tell us who you are — we'll show you what matters most 🎯
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{role.emoji}</div>
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 900, color: "white", margin: "0 0 6px" }}>
                {role.fun}
              </h2>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                Here's what EcoConnect offers you
              </p>
            </>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
            {[1,2].map(s => (
              <div key={s} style={{
                height: 4, borderRadius: 2,
                width: step >= s ? 24 : 8,
                background: step >= s ? "#4ade80" : "rgba(255,255,255,0.2)",
                transition: "all 0.3s ease",
              }} />
            ))}
          </div>
        </div>

        <div style={{ padding: "28px 32px 32px" }}>
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: 13, color: "#6b7280", fontFamily: "'DM Sans', sans-serif", margin: "0 0 4px", fontWeight: 600 }}>
                SELECT YOUR ROLE
              </p>
              {roles.map((r) => (
                <div key={r.id}
                  onClick={() => handleSelect(r.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 16,
                    padding: "16px 20px", borderRadius: 14,
                    border: `2px solid ${selected === r.id ? r.color : "#f1f5f9"}`,
                    background: selected === r.id ? r.bg : "white",
                    cursor: "pointer", transition: "all 0.2s",
                    transform: selected === r.id ? "scale(1.02)" : "scale(1)",
                  }}
                  onMouseEnter={(e) => { if (selected !== r.id) e.currentTarget.style.borderColor = r.color + "60"; }}
                  onMouseLeave={(e) => { if (selected !== r.id) e.currentTarget.style.borderColor = "#f1f5f9"; }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: r.bg, border: `1.5px solid ${r.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, flexShrink: 0,
                  }}>
                    {r.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>{r.title}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>{r.subtitle}</div>
                  </div>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${selected === r.id ? r.color : "#e5e7eb"}`,
                    background: selected === r.id ? r.color : "white",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all 0.2s",
                  }}>
                    {selected === r.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "white" }} />}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 2 && role && (
            <div style={{ opacity: animating ? 0 : 1, transition: "opacity 0.3s" }}>
              <div style={{ background: role.bg, border: `1.5px solid ${role.border}`, borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 800, color: role.color, letterSpacing: 1, textTransform: "uppercase", fontFamily: "'Sora', sans-serif", margin: "0 0 14px" }}>
                  WHAT YOU GET
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {role.features.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: role.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ color: "white", fontSize: 10, fontWeight: 700 }}>✓</span>
                      </div>
                      <span style={{ fontSize: 14, color: "#374151", fontFamily: "'DM Sans', sans-serif" }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={() => { setStep(1); setSelected(null); }}
                  style={{ flex: 1, padding: "12px", background: "#f8fafc", color: "#6b7280", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 13, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer" }}>
                  ← Back
                </button>
                <button onClick={handleCta}
                  style={{ flex: 2, padding: "12px", background: role.color, color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer", transition: "all 0.2s", boxShadow: `0 4px 16px ${role.color}40` }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.opacity = "0.9"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.opacity = "1"; }}
                >
                  {role.cta} →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(32px) scale(0.96) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
    </div>
  );
}

function ExecutionDropdown({ onClose }) {
  const navigate = useNavigate();
  return (
    <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", background: "white", boxShadow: "0 16px 48px rgba(0,0,0,0.10)", borderRadius: "16px", border: "1px solid #f1f5f9", zIndex: 500, padding: "12px", minWidth: "320px", animation: "dropDown 0.2s ease" }}>
      <div onClick={() => { navigate("/execution"); onClose(); }}
        style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "14px 16px", borderRadius: "10px", cursor: "pointer", transition: "background 0.15s" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#fff7ed"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
      >
        <div>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: "14px", fontWeight: "700", color: "#1a1a2e", margin: "0 0 4px" }}>Ecoconnect Platform Development</p>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12.5px", color: "#6b7280", margin: 0, lineHeight: "1.5" }}>Where verified sustainability expertise meets real-world impact.</p>
        </div>
      </div>
    </div>
  );
}

function MegaDropdown({ menu, onItemClick }) {
  return (
    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", boxShadow: "0 16px 48px rgba(0,0,0,0.10)", borderTop: `3px solid ${menu.color}`, zIndex: 500, padding: "36px 56px", display: "grid", gridTemplateColumns: `repeat(${menu.sections.length}, 1fr)`, gap: "36px", animation: "dropDown 0.2s ease" }}>
      {menu.sections.map((section, i) => (
        <div key={i}>
          <p onClick={() => onItemClick(menu.basePath, section.categoryKey)}
            style={{ fontSize: "11px", fontWeight: "800", color: menu.color, fontFamily: "Sora,sans-serif", textTransform: "uppercase", letterSpacing: "1.2px", borderBottom: `1px solid #f1f5f9`, paddingBottom: "10px", margin: "0 0 14px", cursor: "pointer" }}>
            {section.title}
          </p>
          {section.items.map((item, j) => (
            <div key={j} onClick={() => onItemClick(menu.basePath, section.categoryKey)}
              style={{ fontSize: "13.5px", color: "#374151", padding: "7px 0", cursor: "pointer", fontFamily: "Inter,sans-serif", transition: "all 0.15s", borderBottom: j < section.items.length - 1 ? "1px solid #f9fafb" : "none" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = menu.color; e.currentTarget.style.paddingLeft = "8px"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#374151"; e.currentTarget.style.paddingLeft = "0"; }}
            >
              › {item}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Navbar({ onCategorySelect }) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);
  const [activeMenu, setActiveMenu] = useState(null);
  const hoverTimer = useRef(null);
  const [showTour, setShowTour] = useState(false);
  const { totalItems, setIsCartOpen } = useCart();
  const [userLocation, setUserLocation] = useState("Sector 74A, Gurugram");

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 10);
      setVisible(y < lastY.current || y < 80);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // ✅ Page load pe location fetch karo
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        );
        const data = await res.json();
        const area = data.address.suburb || data.address.neighbourhood || data.address.city_district || data.address.city || "Your Location";
        setUserLocation(area);
      });
    }

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMenuEnter = (id) => {
    const menu = megaMenu.find((m) => m.id === id);
    if (!menu) return;
    if (menu.type === "project" || menu.sections.length > 0) {
      clearTimeout(hoverTimer.current);
      setActiveMenu(id);
    }
  };

  const handleMenuLeave = () => {
    hoverTimer.current = setTimeout(() => setActiveMenu(null), 200);
  };

  const handleItemClick = (basePath, categoryKey) => {
    setActiveMenu(null);
    navigate(`/${basePath}?category=${categoryKey}`);
  };

  const openMaps = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          window.open(
            `https://www.google.com/maps?q=${latitude},${longitude}`,
            "_blank", "noreferrer"
          );
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await res.json();
          const area = data.address.suburb || data.address.neighbourhood || data.address.city_district || data.address.city || "Your Location";
          setUserLocation(area);
        },
        () => {
          window.open("https://maps.google.com/?q=DCGI-1104,DLF+Corporate+Greens,Sector+74A,Gurugram,Haryana+122004", "_blank", "noreferrer");
        }
      );
    }
  };

  return (
    <>
      {showTour && <PlatformTour onClose={() => setShowTour(false)} />}

      <header style={{
        position: "sticky", top: 0, zIndex: 300,
        transform: visible ? "translateY(0)" : "translateY(-100%)",
        transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        background: "white",
        boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.08)" : "0 1px 0 #e5e7eb",
      }}>

        <div className="announce-bar">
          <div>
            <strong>Ecoconnect Business</strong> — AI-Powered Energy Ecosystem.
            Deploy Faster. Source Smarter. Scale Seamlessly.
          </div>
          <div className="announce-bar-right">
            <button style={linkStyle}onClick={() => navigate("/contact")}>Support</button>
            <button style={linkStyle} onClick={() => navigate("/contact")}>Become a Partner</button>
            <button style={linkStyle}>🇮🇳 India</button>
            <Link to="/contact">
              <button className="announce-btn">Get Free Consultation</button>
            </Link>
          </div>
        </div>

        <nav className="main-navbar" style={{ padding: "6px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
              <div
                style={{ transition: "transform 0.25s ease, filter 0.25s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.filter = "drop-shadow(0 4px 12px rgba(22,163,74,0.25))"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.filter = "none"; }}
              >
                <EcoLogo height={44} />
              </div>
            </Link>
            <button onClick={openMaps}
              style={{ background: "none", border: "1.5px solid #e5e7eb", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px", marginLeft: "4px" }}>
              <span style={{ fontSize: "16px", color: "#16a34a" }}>📍</span>
              <div style={{ textAlign: "left" }}>
                <span style={{ fontSize: "11px", color: "#6b7280", display: "block" }}>Deliver to</span>
                <strong style={{ fontSize: "13px", color: "#111827", fontWeight: "600" }}>{userLocation} ›</strong>
              </div>
            </button>
          </div>

          <div className="nav-search">
            <input type="text" placeholder="Search EV chargers, solar panels, IoT devices, services..." />
            <button className="nav-search-btn">🔍</button>
          </div>

          <div className="nav-actions">
            <Link to="/login" style={{ textDecoration: "none" }}>
              <button className="nav-action-btn">
                <span>👤</span>
                <small>Hello, User</small>
                <strong>Login / Register</strong>
              </button>
            </Link>

            <Link to="/enquiries" style={{ textDecoration: "none" }}>
              <button className="nav-action-btn">
                <span>📋</span>
                <small>Track</small>
                <strong>My Enquiries</strong>
              </button>
            </Link>

            <button className="nav-action-btn" onClick={() => setIsCartOpen(true)} style={{ position: "relative" }}>
              <span style={{ fontSize: "20px" }}>🛒</span>
              <strong style={{ fontSize: "13px", whiteSpace: "nowrap" }}>My Cart</strong>
              {totalItems > 0 && (
                <div style={{ position: "absolute", top: "4px", right: "4px", background: "#2d6a4f", color: "white", borderRadius: "50%", width: "18px", height: "18px", fontSize: "10px", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Sora', sans-serif" }}>
                  {totalItems}
                </div>
              )}
            </button>

            <button className="btn-get-started" onClick={() => setShowTour(true)}>
              Get Started
            </button>
          </div>
        </nav>

        <div style={{ position: "relative", background: "white" }} onMouseLeave={handleMenuLeave}>
          <nav style={{ display: "flex", alignItems: "center", justifyContent: "center", borderTop: "1px solid #f1f5f9", padding: "0 40px" }}>
            {megaMenu.map((menu) => {
              const isActive = activeMenu === menu.id;
              return (
                <div key={menu.id}
                  onMouseEnter={() => handleMenuEnter(menu.id)}
                  onClick={() => { if (menu.directLink) navigate(menu.directLink); }}
                  style={{ position: "relative", padding: "16px 40px", cursor: "pointer", borderBottom: isActive ? `3px solid ${menu.color}` : "3px solid transparent", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ fontSize: "15px", fontWeight: "700", fontFamily: "Sora,sans-serif", color: isActive ? menu.color : "#1e293b", transition: "color 0.2s", letterSpacing: "-0.2px" }}>
                    {menu.label}
                  </span>
                  {(menu.sections.length > 0 || menu.type === "project" || menu.showArrow) && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                      style={{ transition: "transform 0.25s ease", transform: isActive ? "rotate(180deg)" : "rotate(0deg)" }}>
                      <path d="M2.5 5L7 9.5L11.5 5" stroke={isActive ? menu.color : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              );
            })}
          </nav>

          {activeMenu && (() => {
            const menu = megaMenu.find((m) => m.id === activeMenu);
            if (!menu) return null;
            if (menu.type === "project") {
              return (
                <div onMouseEnter={() => clearTimeout(hoverTimer.current)} onMouseLeave={handleMenuLeave} style={{ position: "relative" }}>
                  <ExecutionDropdown onClose={() => setActiveMenu(null)} />
                </div>
              );
            }
            if (menu.sections.length > 0) {
              return (
                <div onMouseEnter={() => clearTimeout(hoverTimer.current)} onMouseLeave={handleMenuLeave}>
                  <MegaDropdown menu={menu} onItemClick={handleItemClick} />
                </div>
              );
            }
            return null;
          })()}
        </div>

        <style>{`
          @keyframes dropDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </header>
    </>
  );
}

export default Navbar;